/**
 * Error Boundary for AI Agent
 * Catches and handles component errors
 */
import React, { Component, ErrorInfo } from 'react';
import { Alert, Button, Card } from 'antd';
import { ReloadOutlined, HomeOutlined } from '@ant-design/icons';
import styled from 'styled-components';

const ErrorContainer = styled.div`
  padding: 24px;
  text-align: center;
`;

const ErrorDetails = styled.pre`
  text-align: left;
  background: var(--color-gray-100);
  padding: 16px;
  border-radius: 8px;
  overflow: auto;
  max-height: 200px;
  margin: 16px 0;
  font-size: 12px;
`;

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
}

export class AIAgentErrorBoundary extends Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = {
    hasError: false,
    error: null,
    errorInfo: null,
    showDetails: false,
  };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('AI Agent Error:', error, errorInfo);
    
    // Log to error tracking service
    this.logErrorToService(error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });
  }

  logErrorToService = (error: Error, errorInfo: ErrorInfo) => {
    // Send to error tracking service
    const errorData = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
    };
    
    // Would send to service like Sentry
    console.error('Error logged:', errorData);
  };

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorContainer>
          <Card>
            <Alert
              message="AI Assistant Error"
              description="Something went wrong with the AI Assistant. Don't worry, your data is safe."
              type="error"
              showIcon
            />
            
            <div style={{ marginTop: 24 }}>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={this.handleReset}
                style={{ marginRight: 8 }}
              >
                Try Again
              </Button>
              
              <Button
                icon={<HomeOutlined />}
                onClick={() => window.location.href = '/'}
              >
                Go to Dashboard
              </Button>
            </div>
            
            <Button
              type="link"
              size="small"
              onClick={() => this.setState({ showDetails: !this.state.showDetails })}
              style={{ marginTop: 16 }}
            >
              {this.state.showDetails ? 'Hide' : 'Show'} Technical Details
            </Button>
            
            {this.state.showDetails && (
              <ErrorDetails>
                {this.state.error?.toString()}
                {this.state.errorInfo?.componentStack}
              </ErrorDetails>
            )}
          </Card>
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}